import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Icon, type IconName } from "../icon";
import { ButtonSpinner } from "../spinner";

const iconButtonVariants = cva(
  [
    "inline-flex",
    "items-center",
    "justify-center",
    "shrink-0",
    "rounded-(--radius-sm)",
    "border",
    "border-solid",
    "cursor-pointer",
    "select-none",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "outline-none",
    "[&_svg]:pointer-events-none",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-button-primary-default-background",
          "text-button-primary-default-text",
          "border-button-primary-default-background",
          "hover:bg-button-primary-hover-background",
          "hover:border-button-primary-hover-background",
          "active:bg-button-primary-pressed-background",
          "active:border-button-primary-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        outline: [
          "bg-button-outline-default-background",
          "text-button-outline-default-text",
          "border-button-outline-default-border",
          "hover:bg-button-outline-hover-background",
          "hover:border-button-outline-hover-border",
          "active:bg-button-outline-pressed-background",
          "active:border-button-outline-pressed-border",
          "data-[mouse-down]:bg-button-outline-pressed-background",
          "data-[mouse-down]:border-button-outline-pressed-border",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        ghost: [
          "bg-transparent",
          "text-button-ghost-default-text",
          "border-transparent",
          "hover:bg-button-ghost-hover-background",
          "active:bg-button-ghost-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        info: [
          "bg-button-info-default-background",
          "text-button-info-default-text",
          "border-button-info-default-background",
          "hover:bg-button-info-hover-background",
          "hover:border-button-info-hover-background",
          "active:bg-button-info-pressed-background",
          "active:border-button-info-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
      },
      intent: {
        default: null,
        destructive: null,
      },
      size: {
        sm: ["size-(--spacing-32)"],
        md: ["size-(--spacing-40)"],
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        intent: "destructive",
        class: [
          "bg-button-primary-destructive-default-background",
          "text-button-primary-destructive-text",
          "border-button-primary-destructive-default-background",
          "hover:bg-button-primary-destructive-hover-background",
          "hover:border-button-primary-destructive-hover-background",
          "active:bg-button-primary-destructive-pressed-background",
          "active:border-button-primary-destructive-pressed-background",
        ],
      },
      {
        variant: "ghost",
        intent: "destructive",
        class: ["text-text-destructive"],
      },
    ],
    defaultVariants: {
      variant: "primary",
      intent: "default",
      size: "md",
    },
  },
);

export interface IconButtonProps
  extends Omit<ButtonPrimitive.Props, "className" | "children">,
    VariantProps<typeof iconButtonVariants> {
  /** The icon to display */
  icon: IconName;
  /** Accessible label (required — no visible text) */
  "aria-label": string;
  /** Loading state — shows spinner, disables interaction */
  loading?: boolean;
}

/**
 * Icon-only button for compact actions. Requires `aria-label` for accessibility.
 *
 * @example
 * <IconButton icon="settings-cog" aria-label="Settings" />
 * <IconButton variant="ghost" icon="x-close" aria-label="Close" />
 * <IconButton intent="destructive" icon="trash-delete-bin" aria-label="Delete" />
 */
function IconButton({
  icon,
  variant,
  intent,
  size,
  loading = false,
  disabled = false,
  ...props
}: IconButtonProps) {
  const isDisabled = disabled || loading;
  const iconSize = "sm";

  return (
    <ButtonPrimitive
      data-slot="icon-button"
      data-variant={variant}
      data-size={size}
      className={cn(
        iconButtonVariants({ variant, intent, size }),
        isDisabled && "opacity-40 pointer-events-none",
        disabled && "cursor-not-allowed",
        loading && !disabled && "cursor-default",
      )}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      tabIndex={isDisabled ? -1 : undefined}
      {...props}
    >
      {loading ? (
        <ButtonSpinner size={iconSize} />
      ) : (
        <Icon name={icon} size={iconSize} tone="inherit" />
      )}
    </ButtonPrimitive>
  );
}

export { IconButton };
